import { useMemo, type MouseEvent } from 'react';
import { Building2, Calendar, MapPin, DollarSign, Trophy, CheckCircle2, Users, ClipboardList } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDateRange, formatCurrency } from '@/utils/formatters';
import { getEventDisplayTitle, sortEventsByDisplayTitle } from '@/utils/eventTypes';
import { useEventsPrizeTotal, type PrizeEventLike } from '@/api/extensions/usePrizeOps';

/**
 * Per-rodeo registration summary computed by the parent (RodeosPage) and
 * passed in. We accept it pre-aggregated so the card stays a pure render.
 *
 *  - selfEvents: count of events the current user is registered for
 *  - groupName / groupAttached: whether the user has a buddy group bound
 *    to this rodeo (used as the headline cue when not personally
 *    registered yet)
 *  - groupRegistrations: total active registrations linked to the user's
 *    buddy group across this rodeo, plus how many distinct events those
 *    registrations cover (only meaningful when a buddy group is attached)
 */
export interface RodeoRegistrationSummary {
  selfEvents: number;
  groupAttached: boolean;
  groupName?: string | null;
  groupRegistrations?: { totalRegs: number; eventCount: number } | null;
}

/**
 * Buddy-group action surfaced on the rodeo card so users can register or
 * manage a group without drilling into the rodeo detail page. Resolved by
 * the parent (RodeosPage) and passed in pre-computed; the card is a pure
 * render. Hidden on cards where buddy groups aren't enabled on the rodeo
 * itself, regardless of whether this prop is supplied.
 *
 *  - registered_primary — user owns a group attached to this rodeo
 *  - registered_member  — user is a non-primary member of the attached group
 *  - can_register       — user has at least one eligible primary group
 *  - must_create        — user has no eligible group; CTA seeds creation
 */
export type BuddyGroupCardActionState =
  | 'registered_primary'
  | 'registered_member'
  | 'can_register'
  | 'must_create';

export interface BuddyGroupCardAction {
  state: BuddyGroupCardActionState;
  /** Click handler. Receives the synthetic event so the caller can
      stopPropagation() to avoid the card's outer onClick firing. */
  onAction: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Optional secondary click handler. Currently used for the
      must_create state when the user is also a non-primary member of
      another group: the primary action stays "Create a new group for
      this rodeo" and the secondary opens "View my existing groups"
      so they can find their primary cardholder and follow up. */
  onSecondaryAction?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Group name for the registered-primary/member states. */
  attachedGroupName?: string | null;
  /** Eligible primary-group count for the can_register CTA copy. */
  eligibleCount?: number;
  /** Count of buddy groups the user is in as a NON-primary member that
      aren't attached to this rodeo. Used to redirect the must_create
      copy from "create one" to "ask your primary OR create your own"
      when the user is already in a group but can't attach it
      themselves. Mirrors the RodeoDetailPage hint so the list cards
      don't tell a member they have no buddy group. */
  memberOnlyCount?: number;
}

interface JackpotCardProps {
  jackpot: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
    is_published?: boolean;
    events?: any[];
    organization_name?: string;
    location?: string;
    buddy_groups_enabled?: boolean;
    /** Finals rodeos never offer buddy groups — suppresses the action row. */
    is_finals?: boolean;
    /** Server-aggregated count of active (non-cancelled) event_registrations
     *  across every event in this rodeo. Rendered as the "X entries" chip
     *  in the quick-stats row. Undefined on records served by code paths
     *  that don't yet emit the field (legacy/cached payloads). */
    total_event_entries?: number;
    [key: string]: unknown;
  };
  onClick: () => void;
  showPublished?: boolean;
  registrationSummary?: RodeoRegistrationSummary | null;
  buddyGroupAction?: BuddyGroupCardAction | null;
  /** When set, renders an explicit detail-page CTA in the card's bottom-
      right corner. Label is caller-controlled (e.g. "View Rodeo Events"
      for the rodeos list, "View Jackpot" for the jackpots list) so the
      button always reads naturally for its host page. */
  viewActionLabel?: string;
}

export function JackpotCard({
  jackpot,
  onClick,
  showPublished,
  registrationSummary,
  buddyGroupAction,
  viewActionLabel,
}: JackpotCardProps) {
  const events = jackpot.events ?? [];
  const eventCount = events.length;

  // Badges are a set, not a schedule — the API hands them back in row order,
  // which reads as random on the card. Alphabetise for scanning. (Ordering on
  // day-sheet / draw / live-scoring stays run-ordered and is untouched.)
  const sortedEvents = useMemo(() => sortEventsByDisplayTitle(events), [events]);

  // Aggregate prize across events — live-computed for dynamic events that show
  // their prize, static otherwise. `requireDisplay: false` sums every event
  // regardless of its per-event "Contestants" toggle, so the card shows the
  // true full rodeo prize total (matching the "My Rodeos" header), rather than
  // gating the total on which events have their contestants display turned on.
  const totalPrize = useEventsPrizeTotal(events as PrizeEventLike[], {
    requireDisplay: false,
  });
  const feeRange = events.reduce(
    (acc: { min: number; max: number }, e: any) => {
      const fee = Number(e.entry_fee) || 0;
      return { min: Math.min(acc.min, fee), max: Math.max(acc.max, fee) };
    },
    { min: Infinity, max: 0 }
  );
  const feeDisplay =
    eventCount === 0
      ? null
      : feeRange.min === feeRange.max
        ? formatCurrency(feeRange.min)
        : `${formatCurrency(feeRange.min)} - ${formatCurrency(feeRange.max)}`;

  const summary = registrationSummary ?? null;
  const groupRegs = summary?.groupRegistrations ?? null;
  // Show the registration banner whenever the user has any personal stake:
  // they're registered, their buddy group has at least one registration,
  // or they've attached a buddy group to this rodeo.
  const hasPersonalRegistration =
    !!summary &&
    ((summary.selfEvents ?? 0) > 0 ||
      (groupRegs?.totalRegs ?? 0) > 0 ||
      summary.groupAttached);

  return (
    <Card
      className={cn(
        'cursor-pointer hover:shadow-md hover:border-primary/30 transition-all duration-200 overflow-hidden h-full flex flex-col',
        hasPersonalRegistration && 'bg-primary/5 border-primary/40',
      )}
      onClick={onClick}
      aria-label={
        hasPersonalRegistration
          ? `${jackpot.name} — you have registrations`
          : undefined
      }
    >
      <CardContent className="p-0 flex flex-col flex-1">
        {/* ─── Top accent bar ─── */}
        <div className="h-1 bg-primary" />

        {/* `gap-3` — NOT `space-y-3` — because Tailwind's space-y-*
            applies a fixed `margin-top` to every child after the first,
            which overrides `mt-auto` on the bottom block and breaks
            bottom-alignment across cards in a row. `gap` lives on the
            container instead, so it leaves the bottom item's auto margin
            free to consume the row's leftover space. */}
        <div className="p-5 flex flex-col gap-3 flex-1">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-base leading-tight line-clamp-2">
              {jackpot.name}
            </h3>
            {showPublished && !jackpot.is_published && (
              <Badge variant="outline" className="shrink-0 text-xs">
                Draft
              </Badge>
            )}
          </div>

          {/* Registration summary banner — only renders when the user has
              a personal stake (their own registrations, a buddy group on
              the rodeo, or buddy-group registrations). Renders BEFORE the
              date/location so it's the first thing the eye lands on after
              the title. */}
          {hasPersonalRegistration && summary && (
            <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-2 text-xs">
              <CheckCircle2
                className="h-4 w-4 shrink-0 text-primary mt-px"
                aria-hidden="true"
              />
              <div className="space-y-0.5 min-w-0">
                {summary.selfEvents > 0 && (
                  <p className="font-medium text-foreground">
                    Entered in {summary.selfEvents} of {eventCount} event
                    {eventCount === 1 ? '' : 's'}
                  </p>
                )}
                {summary.groupAttached && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Users className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      Buddy group
                      {summary.groupName ? `: ${summary.groupName}` : ''} attached
                    </span>
                  </p>
                )}
                {groupRegs && groupRegs.totalRegs > 0 && (
                  <p className="text-muted-foreground">
                    Group has {groupRegs.totalRegs} entr
                    {groupRegs.totalRegs === 1 ? 'y' : 'ies'} across{' '}
                    {groupRegs.eventCount} event
                    {groupRegs.eventCount === 1 ? '' : 's'}
                  </p>
                )}
                {summary.selfEvents === 0 &&
                  summary.groupAttached &&
                  (!groupRegs || groupRegs.totalRegs === 0) && (
                    <p className="text-muted-foreground">
                      No event registrations yet
                    </p>
                  )}
              </div>
            </div>
          )}

          {/* Date & location */}
          <div className="space-y-1 text-sm text-muted-foreground">
            {(jackpot.start_date || jackpot.end_date) && (
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span>{formatDateRange(jackpot.start_date, jackpot.end_date)}</span>
              </div>
            )}
            {jackpot.organization_name && (
              <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{jackpot.organization_name}</span>
              </div>
            )}
            {jackpot.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{jackpot.location}</span>
              </div>
            )}
          </div>

          {/* Quick stats row */}
          {eventCount > 0 && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
              {feeDisplay && (
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3 text-emerald-500" />
                  {feeDisplay}
                </span>
              )}
              {totalPrize > 0 && (
                <span className="flex items-center gap-1">
                  <Trophy className="h-3 w-3 text-amber-500" />
                  {formatCurrency(totalPrize)}
                </span>
              )}
              {typeof jackpot.total_event_entries === 'number' && (
                <span
                  className="flex items-center gap-1"
                  aria-label={`${jackpot.total_event_entries} total entries across all events`}
                >
                  <ClipboardList className="h-3 w-3 text-sky-500" />
                  {jackpot.total_event_entries} entr
                  {jackpot.total_event_entries === 1 ? 'y' : 'ies'}
                </span>
              )}
            </div>
          )}

          {/* Event badges */}
          {eventCount > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="event-badges">
              {sortedEvents.map((e: any) => (
                <Badge
                  key={e.id}
                  variant="secondary"
                  className="text-xs font-normal"
                >
                  {getEventDisplayTitle(e)}
                  {e.entry_fee != null && (
                    <span className="ml-1 opacity-60">{formatCurrency(e.entry_fee)}</span>
                  )}
                </Badge>
              ))}
            </div>
          )}

          {/* Bottom block — buddy-group action + view CTA. Wrapped in a
              `mt-auto` flex column so the whole block sticks to the bottom
              of the card regardless of how much content sits above it. This
              keeps a row of cards visually aligned even when one rodeo has
              more events / longer name than its neighbours. */}
          {(viewActionLabel ||
            (buddyGroupAction && jackpot.buddy_groups_enabled && !jackpot.is_finals)) && (
            <div className="mt-auto pt-3 space-y-2">
              {/* Buddy-group action row — only shown for rodeos that allow
                  buddy groups AND when the parent supplied an action. We
                  split the label/CTA across two lines so the button stays
                  right-aligned even when the rodeo title pushes the card
                  narrow. The button's click is stopped at the row so the
                  outer card's onClick doesn't also fire. */}
              {buddyGroupAction && jackpot.buddy_groups_enabled && !jackpot.is_finals && (
                <div
                  className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2.5 py-2 text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground mt-0.5" />
                    <div className="min-w-0">
                      {buddyGroupAction.state === 'registered_primary' && (
                        <>
                          <p className="font-medium truncate">
                            Buddy group entered
                          </p>
                          <p className="text-muted-foreground truncate">
                            {buddyGroupAction.attachedGroupName
                              ? `${buddyGroupAction.attachedGroupName} · You're primary`
                              : "You're the primary cardholder"}
                          </p>
                        </>
                      )}
                      {buddyGroupAction.state === 'registered_member' && (
                        <>
                          <p className="font-medium truncate">
                            Entered with{' '}
                            {buddyGroupAction.attachedGroupName ?? 'a buddy group'}
                          </p>
                          <p className="text-muted-foreground truncate">
                            Member — view group details
                          </p>
                        </>
                      )}
                      {buddyGroupAction.state === 'can_register' && (
                        <>
                          <p className="font-medium truncate">No buddy group yet</p>
                          <p className="text-muted-foreground truncate">
                            {(buddyGroupAction.eligibleCount ?? 0) === 1
                              ? '1 eligible group ready to enter'
                              : `${buddyGroupAction.eligibleCount ?? 0} eligible groups ready to enter`}
                          </p>
                        </>
                      )}
                      {buddyGroupAction.state === 'must_create' && (
                        (buddyGroupAction.memberOnlyCount ?? 0) > 0 ? (
                          <>
                            <p className="font-medium truncate">
                              You&apos;re in a buddy group
                            </p>
                            <p className="text-muted-foreground">
                              Ask your primary to enter it for this rodeo, or
                              create your own.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium truncate">No buddy group yet</p>
                            <p className="text-muted-foreground truncate">
                              Create one to enter together
                            </p>
                          </>
                        )
                      )}
                    </div>
                  </div>
                  {/* Action cluster — most states surface a single button.
                      The must_create + memberOnlyCount > 0 case is the
                      exception: the user has BOTH options (view their
                      existing groups so they can ask the primary, or
                      create a new group), so we render two compact
                      buttons stacked side-by-side. */}
                  {buddyGroupAction.state === 'must_create' &&
                  (buddyGroupAction.memberOnlyCount ?? 0) > 0 &&
                  buddyGroupAction.onSecondaryAction ? (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-xs"
                        onClick={buddyGroupAction.onSecondaryAction}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 px-2.5 text-xs"
                        onClick={buddyGroupAction.onAction}
                      >
                        Create
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant={
                        buddyGroupAction.state === 'registered_primary' ||
                        buddyGroupAction.state === 'registered_member'
                          ? 'outline'
                          : 'default'
                      }
                      className="h-7 px-2.5 text-xs shrink-0"
                      onClick={buddyGroupAction.onAction}
                    >
                      {buddyGroupAction.state === 'registered_primary' && 'Manage'}
                      {buddyGroupAction.state === 'registered_member' && 'View'}
                      {buddyGroupAction.state === 'can_register' && 'Enter'}
                      {buddyGroupAction.state === 'must_create' && 'Create'}
                    </Button>
                  )}
                </div>
              )}

              {/* Detail-page CTA — full-width primary button below the
                  buddy-group row. The whole card is also clickable, but
                  the explicit button is the visual anchor at the foot of
                  every card. Stops propagation only as housekeeping;
                  navigation is idempotent either way. */}
              {viewActionLabel && (
                <Button
                  size="sm"
                  variant="default"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                >
                  {viewActionLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
